import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Phone, MapPin } from "lucide-react";
import Navbar from "../components/sections/Navbar";
import Footer from "../components/sections/Footer";
import FloatingCTA from "../components/FloatingCTA";
import { CITIES, citySlug, cityName } from "../data/cities";

export default function CityPage() {
  const { city: slug } = useParams();
  const city = cityName(slug);
  const valid = CITIES.some((c) => citySlug(c) === slug);

  useEffect(() => {
    if (!valid) return;
    document.title = `Badkamer renoveren in ${city} — Master-Bathrooms`;
    const meta = document.querySelector('meta[name="description"]');
    if (meta)
      meta.setAttribute(
        "content",
        `Master-Bathrooms renoveert badkamers, toiletten en inloopdouches in ${city}. Gratis, vrijblijvend adviesgesprek bij u thuis. Bel 06 87445044.`
      );
  }, [city, valid]);

  if (!valid) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navbar />
      <main className="pt-28 md:pt-32">
        <section className="max-w-6xl mx-auto px-5 md:px-8 py-12 md:py-16">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#1E5BC6] font-semibold mb-8 hover:underline"
          >
            <ArrowLeft size={14} /> Terug naar home
          </Link>
          <div className="flex items-center gap-3 tag-overline">
            <MapPin size={14} className="text-[#1E5BC6]" /> {city}
          </div>
          <h1 className="mt-4 text-5xl md:text-7xl text-[#0A0A0A] uppercase tracking-tight leading-[1.02] max-w-5xl">
            Badkamer renoveren in{" "}
            <span className="text-[#1E5BC6]">{city}</span>
          </h1>
          <p className="mt-8 text-lg md:text-xl text-[#5A5A5A] max-w-3xl leading-relaxed">
            Master-Bathrooms is uw specialist voor complete badkamerrenovaties,
            inloopdouches en toiletten in {city} en omstreken. Wij komen gratis
            en vrijblijvend bij u langs voor een adviesgesprek op locatie —
            geen verkooppraatjes, wel een eerlijke prijsopgave op maat.
          </p>
        </section>

        <section className="max-w-6xl mx-auto px-5 md:px-8 pb-16">
          <div className="aspect-[16/9] overflow-hidden">
            <img
              src="/brand/services-v2/complete-badkamer-v4.png"
              alt={`Badkamerrenovatie ${city}`}
              className="w-full h-full object-cover"
            />
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-5 md:px-8 pb-16 space-y-8 text-[#1A1A1A]">
          <div>
            <h2 className="text-2xl md:text-3xl uppercase tracking-tight font-extrabold">
              Vakmanschap in {city}
            </h2>
            <p className="mt-4 text-[#5A5A5A] leading-relaxed">
              Of u nu een gedateerde badkamer wilt vernieuwen, een luxueuze
              inloopdouche wilt installeren of uw toilet wilt renoveren — in{" "}
              {city} bent u bij Master-Bathrooms aan het juiste adres. Wij
              regelen alles van A tot Z, met één aanspreekpunt en één eindprijs
              vooraf. Geen onaangename verrassingen — wel vakkundig uitvoerd werk
              met garantie.
            </p>
          </div>
          <div>
            <h2 className="text-2xl md:text-3xl uppercase tracking-tight font-extrabold">
              Onze werkwijze in {city}
            </h2>
            <p className="mt-4 text-[#5A5A5A] leading-relaxed">
              Wij plannen graag een gratis adviesgesprek bij u thuis in {city}.
              Samen doorlopen we uw wensen, meten we de ruimte op en bespreken
              we materialen. Binnen enkele dagen ontvangt u een helder
              ontwerpvoorstel en een eerlijke prijsopgave. Na akkoord starten we
              op afgesproken planning en werken we door tot een schone
              oplevering.
            </p>
          </div>
          <div>
            <h2 className="text-2xl md:text-3xl uppercase tracking-tight font-extrabold">
              Diensten in {city}
            </h2>
            <ul className="mt-4 grid sm:grid-cols-2 gap-3">
              {[
                ["inloopdouche-plaatsen", "Inloopdouche plaatsen"],
                ["badkamer-renoveren", "Complete badkamer renovatie"],
                ["toilet-renoveren", "Toilet renoveren"],
                ["zakelijke-toiletrenovatie", "Zakelijke toiletrenovatie"],
              ].map(([s, t]) => (
                <li key={s}>
                  <Link
                    to={`/${s}`}
                    className="block border border-[#E5E7EB] p-4 hover:border-[#1E5BC6] hover:bg-white transition-colors text-sm font-bold uppercase tracking-wide text-[#0A0A0A]"
                  >
                    {t} in {city}
                    <ArrowRight size={14} className="inline ml-2 text-[#1E5BC6]" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="bg-[#0A0A0A] text-white py-20 md:py-24">
          <div className="max-w-4xl mx-auto px-5 md:px-8 text-center">
            <h2 className="text-4xl md:text-5xl uppercase tracking-tight">
              Uw badkamer in {city} renoveren?
            </h2>
            <p className="mt-6 text-lg text-[#C7C7C7] max-w-xl mx-auto">
              Plan een gratis adviesgesprek — wij komen naar u toe in {city}.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/#contact"
                className="btn-primary-mb px-8 py-4 text-xs inline-flex items-center justify-center gap-2"
              >
                Plan adviesgesprek <ArrowRight size={16} />
              </Link>
              <a
                href="tel:+31687445044"
                className="btn-outline-mb bg-transparent border-white text-white hover:bg-white hover:text-black px-8 py-4 text-xs inline-flex items-center justify-center gap-2"
              >
                <Phone size={16} /> 06 87445044
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <FloatingCTA />
    </div>
  );
}
